import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Modal from "../../../components/ui-components/Modal";
import TextArea from "../../../components/form-components/TextArea";
import { primaryButtonCls, secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import { toast } from "../../../components/ui-components/Toast";
import { ControlCheckFindingsList } from "../controlCheckFindings";
import { useTemplateDiff, useCapabilityCatalog, useAdoptTemplateUpdatesMutation } from "../api";
import { friendlyRoleError } from "../roleFormUtils";

// Sub-project E3, Task 4: compare-with-template + selective adoption.
//
// Opened for one organisation role (from the Organisation table's "Newer
// template" chip, the Review template updates section, or RoleDetailModal's
// lineage line/chip) — fetches that role's GET .../template-diff and lets
// the admin choose, per offered capability, whether to adopt it. Unlike
// OrgRoleWizard (which proposes a brand-new role), this mutates an EXISTING
// active role's capability set, so the same rolecontrol activation-gate can
// trip on the resulting set — the 409 ROLE_CONTROL_CHECK_FAILED handling
// below mirrors StepControlCheck's override flow exactly (same shared
// ControlCheckFindingsList + the same "reason required once critical"
// gating), just against `adoptTemplateUpdates` instead of `createOrgRole`.

// One offered capability row (either an Added-since-baseline or a
// Removed-since-baseline template capability) with its own Adopt/Skip
// segmented toggle. `kind` only drives the leading icon/tone — the
// Adopt/Skip semantics differ underneath (adopting an "add" GAINS the
// capability, adopting a "remove" DROPS it) but read identically to the
// admin: "yes, pull this change in" vs "no, leave my role as-is for this one".
function AdoptToggle({ code, adopted, onSetAdopted }) {
  return (
    <div
      role="group"
      aria-label={`Adopt or skip ${code}`}
      className="inline-flex shrink-0 rounded-ctrl border border-solid border-border overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onSetAdopted(code, true)}
        aria-pressed={adopted}
        className={`inline-flex items-center border-0 box-border px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors ${
          adopted ? "bg-primary text-white" : "bg-transparent text-text-2 hover:bg-surface-2"
        }`}
      >
        Adopt
      </button>
      <button
        type="button"
        onClick={() => onSetAdopted(code, false)}
        aria-pressed={!adopted}
        className={`inline-flex items-center border-0 border-l border-solid border-border box-border px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors ${
          !adopted ? "bg-surface-3 text-text" : "bg-transparent text-text-2 hover:bg-surface-2"
        }`}
      >
        Skip
      </button>
    </div>
  );
}

function DiffRow({ code, kind, capByCode, adopted, onSetAdopted }) {
  const cap = capByCode.get(code);
  return (
    <div
      className="flex items-start justify-between gap-3 py-2.5"
      data-testid={`diff-row-${kind}-${code}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <FontAwesomeIcon
            icon={kind === "add" ? icon({ name: "plus", style: "solid" }) : icon({ name: "minus", style: "solid" })}
            className={`text-[11px] ${kind === "add" ? "text-success" : "text-danger"}`}
          />
          <span className="text-sm text-text">{cap?.name || code}</span>
        </div>
        {cap?.description && <div className="text-xs text-text-2 mt-0.5">{cap.description}</div>}
      </div>
      <AdoptToggle code={code} adopted={adopted} onSetAdopted={onSetAdopted} />
    </div>
  );
}

// Builds { [code]: true } for every code in the list — the default
// selection is "adopt everything the template offers" so a full adoption is
// a single click ("Adopt selected") away; toggling any row to Skip narrows
// it to a partial adoption.
function allAdopted(codes) {
  return Object.fromEntries(codes.map((c) => [c, true]));
}

export default function TemplateDiffModal({ role, onClose }) {
  const roleId = role?.id;
  const diffQ = useTemplateDiff(roleId, { enabled: !!roleId });
  const capsQ = useCapabilityCatalog();

  const capByCode = useMemo(() => {
    const m = new Map();
    for (const c of capsQ.data || []) m.set(c.code, c);
    return m;
  }, [capsQ.data]);

  const diff = diffQ.data;
  const added = useMemo(() => diff?.added || [], [diff]);
  const removed = useMemo(() => diff?.removed || [], [diff]);

  const [adoptAdd, setAdoptAdd] = useState({});
  const [adoptRemove, setAdoptRemove] = useState({});
  const [findings, setFindings] = useState(null); // null until a 409 returns findings
  const [overrideReason, setOverrideReason] = useState("");

  // Re-seed the selection (default: adopt everything offered) whenever a
  // different role's diff loads — keyed on the diff's own identity rather
  // than added/removed arrays so an unrelated re-render doesn't reset
  // in-progress toggling.
  useEffect(() => {
    if (!diff) return;
    setAdoptAdd(allAdopted(added));
    setAdoptRemove(allAdopted(removed));
    setFindings(null);
    setOverrideReason("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diff?.roleId, diff?.baseVersion, diff?.latestVersion]);

  const adoptMut = useAdoptTemplateUpdatesMutation({
    onSuccess: () => {
      toast.success("Template updates adopted");
      onClose();
    },
    onError: (err) => {
      const code = err?.serverBody?.error?.code;
      if (code === "ROLE_CONTROL_CHECK_FAILED") {
        // Stay open, now showing the authoritative server-side findings —
        // same pattern as OrgRoleWizard's createOrgRoleMut.onError.
        setFindings(err.serverBody.error.findings || []);
        return;
      }
      toast.error(friendlyRoleError(err, "adopt template updates"));
    },
  });

  if (!role) return null;

  const chosenAdd = added.filter((c) => adoptAdd[c]);
  const chosenRemove = removed.filter((c) => adoptRemove[c]);
  const totalOffered = added.length + removed.length;
  const totalAdopting = chosenAdd.length + chosenRemove.length;
  const hasCritical = !!findings && findings.some((f) => f.severity === "critical");
  const canSubmit = totalAdopting > 0 && (!hasCritical || overrideReason.trim().length > 0);

  const handleSubmit = () => {
    const body = { id: roleId, add: chosenAdd, remove: chosenRemove };
    if (hasCritical) body.override_reason = overrideReason.trim();
    adoptMut.mutate(body);
  };

  return (
    <Modal
      show={!!role}
      close={onClose}
      title={`Compare "${role.name}" with its template`}
      description={
        diff?.templateCode
          ? `${diff.templateCode} template · currently at v${diff.baseVersion}, latest is v${diff.latestVersion}`
          : undefined
      }
      icon={icon({ name: "code-compare", style: "solid" })}
      cancelButton=""
      confirmButton=""
      size="lg"
    >
      {(diffQ.isLoading || capsQ.isLoading) && <div className="text-sm text-text-2">Loading…</div>}

      {diffQ.isError && !diffQ.isLoading && (
        <div className="text-sm text-danger">Couldn't load the template comparison — try again.</div>
      )}

      {!diffQ.isLoading && !diffQ.isError && diff && (
        <>
          {totalOffered === 0 ? (
            <div className="flex items-center gap-2 text-sm text-success">
              <FontAwesomeIcon icon={icon({ name: "circle-check", style: "solid" })} />
              This role is up to date with its template.
            </div>
          ) : (
            <>
              <div className="text-xs text-text-2 mb-3" data-testid="adopt-summary">
                Adopting {totalAdopting} of {totalOffered} changes
              </div>

              {added.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-1">
                    Added since v{diff.baseVersion}
                  </div>
                  <div className="divide-y divide-border">
                    {added.map((code) => (
                      <DiffRow
                        key={code}
                        code={code}
                        kind="add"
                        capByCode={capByCode}
                        adopted={!!adoptAdd[code]}
                        onSetAdopted={(c, v) => setAdoptAdd((prev) => ({ ...prev, [c]: v }))}
                      />
                    ))}
                  </div>
                </div>
              )}

              {removed.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-1">
                    Removed since v{diff.baseVersion}
                  </div>
                  <div className="divide-y divide-border">
                    {removed.map((code) => (
                      <DiffRow
                        key={code}
                        code={code}
                        kind="remove"
                        capByCode={capByCode}
                        adopted={!!adoptRemove[code]}
                        onSetAdopted={(c, v) => setAdoptRemove((prev) => ({ ...prev, [c]: v }))}
                      />
                    ))}
                  </div>
                </div>
              )}

              {findings && (
                <div className="mt-2 pt-3 border-t border-border">
                  <ControlCheckFindingsList findings={findings} />
                  {hasCritical && (
                    <div className="mt-4">
                      <TextArea
                        isLoaded
                        name="adopt-override-reason"
                        title="Override reason (required to adopt despite the critical finding(s) above)"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="Explain why this adoption should proceed despite the critical finding(s)."
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-border">
        <button type="button" className={secondaryButtonCls} onClick={onClose}>
          Cancel
        </button>
        {totalOffered > 0 && (
          <button
            type="button"
            className={primaryButtonCls}
            onClick={handleSubmit}
            disabled={!canSubmit || adoptMut.isPending}
            title={!canSubmit && hasCritical ? "Provide an override reason to adopt despite the critical finding(s)" : undefined}
          >
            {adoptMut.isPending ? "Adopting…" : hasCritical ? "Override & adopt" : "Adopt selected"}
          </button>
        )}
      </div>
    </Modal>
  );
}

TemplateDiffModal.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};
